import {
	Box,
	Button,
	Divider,
	Icons,
	Inline,
	Stack,
	Text,
} from "@sembark-travel/ui-base"
import {
	Form,
	TextAreaInputField,
	validateFormValues,
	withServerErrors,
	ErrorMessage,
	useFieldValue,
} from "@sembark-travel/ui-form"
import { InferType, number, object, string } from "yup"
import { useXHR } from "@sembark-travel/xhr"
import { ReactNode, useState } from "react"
import { useLocation } from "react-router"
import { searchToQuery } from "@sembark-travel/ui-router"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { useViewportTrigger } from "./store"

type IType = "ai_itinerary_builder" | "package_parser"

const FeedbackSchema = object({
	feedback_status: number()
		.oneOf([0, 1])
		.required("Please select your feedback."),

	feedback_reason: string().when("feedback_status", {
		is: 0,
		then: (schema) => schema.required("Please tell us what went wrong."),
		otherwise: (schema) => schema.optional(),
	}),
})

type IFeedback = InferType<typeof FeedbackSchema>

export default function AiParsingFeedbackDialog({
	enabled,
	children,
	tripId,
}: {
	tripId?: string | number
	enabled?: boolean
	children?: (props: { onClick: () => void }) => ReactNode
}) {
	const location = useLocation()
	const { generationId, job_uuid } = searchToQuery<{
		job_uuid?: string
		generationId?: string
	}>(location?.search)
	const [openDialog, setOpenDialog] = useState(false)
	const [submitted, setSubmitted] = useState(false)

	const uuId = job_uuid || generationId
	const type: IType = generationId ? "ai_itinerary_builder" : "package_parser"

	//useViewportTrigger will observe the targetId and as soon it found it it trigger the func
	useViewportTrigger({
		targetId: "quote_summary",
		enabled,
		minVisibleDuration: 400,
		storageKey: `ai-feedback-form-${uuId}`,
		onTrigger: () => {
			setOpenDialog(true)
		},
		threshold: 0.4,
		once: true,
	})

	function manageDialog(type: "OPEN" | "CLOSE") {
		if (type === "OPEN") {
			setOpenDialog(true)
		} else {
			setOpenDialog(false)
		}
	}

	function onSuccess() {
		setSubmitted(true)
	}

	return (
		<>
			{submitted ? null : children?.({ onClick: () => manageDialog("OPEN") })}
			{openDialog ? (
				<Box
					boxShadow={"lg"}
					backgroundColor={"default"}
					position={"fixed"}
					rounded="lg"
					zIndex="100"
					bottom={"0"}
					right={"0"}
					margin="4"
				>
					<AiParsingFeedbackForm
						tripId={tripId}
						type={type}
						uuId={uuId}
						onSuccess={onSuccess}
						manageDialog={manageDialog}
					/>
				</Box>
			) : null}
		</>
	)
}

interface IAiParsingFeedbackForm {
	manageDialog: (type: "OPEN" | "CLOSE") => void
	tripId?: string | number
	onSuccess?: () => void
	uuId?: string
	type?: IType
}
export function AiParsingFeedbackForm({
	manageDialog,
	onSuccess,
	uuId,
	type,
	tripId,
}: IAiParsingFeedbackForm) {
	const xhr = useXHR()

	return (
		<Form<IFeedback>
			initialValues={{
				feedback_status: undefined,
				feedback_reason: "",
			}}
			validate={validateFormValues(FeedbackSchema)}
			onSubmit={withServerErrors(async (values) => {
				const res = await xhr.post(
					`/trips/${tripId}/submit-parsed-trip-quote-data-feedback`,
					{ ...values, job_uuid: uuId, type }
				)
				manageDialog("CLOSE")
				onSuccess?.()
				showSnackbar(res?.data?.message ?? "Feedback submitted successfully!")
			})}
			subscription={{ submitting: true }}
		>
			{({ handleSubmit, submitting }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack padding="4" gap="1">
						<Stack gap={"1"} alignItems={"start"} justifyContent={"center"}>
							<Text fontWeight="semibold">
								How would you rate this AI-Parsed Quotation?
							</Text>
							<Text color={"muted"} fontSize={"sm"}>
								Your feedback helps us improve future itinerary suggestions.
							</Text>
						</Stack>
						<Inline justifyContent="center" gap="1">
							<Inline justifyContent="center" gap="4">
								<FeedbackOption
									name="feedback_status"
									value={1}
									activeColor="success"
								/>

								<FeedbackOption
									name="feedback_status"
									value={0}
									activeColor="danger"
								/>
							</Inline>
						</Inline>

						<TextAreaInputField
							name="feedback_reason"
							label="What could be improved?"
							rows={2}
							placeholder="Tell us what went wrong..."
						/>

						<ErrorMessage name="feedback_status" />
						<Divider paddingY={"0"} marginX={"0"} sm />
						<Inline gap="4">
							<Button type="submit" disabled={submitting}>
								Submit
							</Button>

							<Button
								level="tertiary"
								disabled={submitting}
								onClick={() => manageDialog("CLOSE")}
							>
								Cancel
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

interface IFeedbackOptionProps<T extends string | number> {
	name: string
	value: T
	activeColor: "success" | "danger"
}

function FeedbackOption<T extends string | number>({
	name,
	value,
	activeColor,
}: IFeedbackOptionProps<T>) {
	const { value: selectedValue, onChange } = useFieldValue<T>(name)

	const selected = selectedValue === value

	return (
		<Box
			as="button"
			type="button"
			onClick={() => onChange(value)}
			display="flex"
			alignItems="center"
			justifyContent="center"
			width="12"
			height="12"
			rounded="full"
			cursor="pointer"
			borderWidth="1"
			borderColor={selected ? activeColor : "default"}
			bgColor={selected ? `${activeColor}` : "inset"}
			style={{
				background: "transparent",
				transition: "all 150ms ease",
			}}
		>
			<Icons.HandThumbUpSolid
				size="8"
				rotate={value === 0 ? "180" : undefined}
				color={selected ? activeColor : "muted"}
			/>
		</Box>
	)
}
